/**
 * Helpers for article scenario regression tests and copy metrics modules.
 * Rounding: dollar amounts use Math.round unless noted.
 */
import { calculateSchedule } from './amortizationEngine';
import type { ScheduleInputs } from './amortizationEngine';
import type { AmortizationEntry } from '../store/calculatorStore';
import type { ShareableTab } from './shareUtils';
import type { PaymentFrequency } from '../types/mortgageTypes';
import { getPaymentsPerYear } from './mortgageCalculations';
import { calculateAffordability } from './affordabilityCalculations';
import type { AffordabilityInputs } from '../types/affordabilityTypes';
import { useCalculatorStore } from '../store/calculatorStore';

export const ARTICLE_SCENARIO_START = new Date('2026-05-01T12:00:00.000Z');

export interface TabScheduleMetrics {
  payment: number;
  totalInterest: number;
  scheduleLength: number;
  firstTotalPay?: number;
  interestMonth1?: number;
  principalMonth1?: number;
  pmiMonth1?: number;
  oneTimePaymentMonth1?: number;
  pmiCancelIndex?: number;
}

export interface TermWindowMetrics {
  termInterest: number;
  termTotalPaid: number;
}

export function scheduleFromShareTab(tab: ShareableTab): ScheduleInputs {
  const startDate =
    tab.mortgageTerms.startDate instanceof Date
      ? tab.mortgageTerms.startDate
      : new Date(String(tab.mortgageTerms.startDate));
  return {
    mortgageTerms: { ...tab.mortgageTerms, startDate },
    annualExpenses: tab.annualExpenses,
    extraPayments: tab.extraPayments,
  };
}

const sumInterest = (s: AmortizationEntry[]) =>
  s.reduce((a, e) => a + e.interestPayment, 0);

export function computeTabScheduleMetrics(tab: ShareableTab): TabScheduleMetrics {
  const schedule = calculateSchedule(scheduleFromShareTab(tab));
  const pmiCancelIndex = schedule.findIndex((e) => (e.pmiPayment ?? 0) === 0);
  const metrics: TabScheduleMetrics = {
    payment: Math.round(schedule[0]?.payment ?? 0),
    totalInterest: Math.round(sumInterest(schedule)),
    scheduleLength: schedule.length,
    firstTotalPay: Math.round(schedule[0]?.totalPayment ?? 0),
    interestMonth1: Math.round(schedule[0]?.interestPayment ?? 0),
    principalMonth1: Math.round(schedule[0]?.principalPayment ?? 0),
    pmiMonth1: Math.round(schedule[0]?.pmiPayment ?? 0),
    oneTimePaymentMonth1: Math.round(schedule[0]?.oneTimePayment ?? 0),
  };
  if (pmiCancelIndex >= 0 && (schedule[0]?.pmiPayment ?? 0) > 0) {
    metrics.pmiCancelIndex = pmiCancelIndex;
  }
  return metrics;
}

export function computeTermWindowMetrics(
  schedule: AmortizationEntry[],
  termYears: number,
  frequency: PaymentFrequency = 'monthly'
): TermWindowMetrics {
  const count = termYears * getPaymentsPerYear(frequency);
  const window = schedule.slice(0, count);
  const termInterest = Math.round(window.reduce((a, e) => a + e.interestPayment, 0));
  const termTotalPaid = Math.round(
    window.reduce((a, e) => a + e.payment + (e.pmiPayment ?? 0), 0)
  );
  return { termInterest, termTotalPaid };
}

export function computeStoreTabMetrics(tabId: string): TabScheduleMetrics | null {
  const tab = useCalculatorStore.getState().calculatorTabs.find((t) => t.id === tabId);
  if (!tab?.data.amortizationSchedule.length) return null;
  const schedule = tab.data.amortizationSchedule;
  const pmiCancelIndex = schedule.findIndex((e) => (e.pmiPayment ?? 0) === 0);
  const metrics: TabScheduleMetrics = {
    payment: Math.round(schedule[0]?.payment ?? 0),
    totalInterest: Math.round(sumInterest(schedule)),
    scheduleLength: schedule.length,
    firstTotalPay: Math.round(schedule[0]?.totalPayment ?? 0),
    interestMonth1: Math.round(schedule[0]?.interestPayment ?? 0),
    principalMonth1: Math.round(schedule[0]?.principalPayment ?? 0),
    pmiMonth1: Math.round(schedule[0]?.pmiPayment ?? 0),
    oneTimePaymentMonth1: Math.round(schedule[0]?.oneTimePayment ?? 0),
  };
  if (pmiCancelIndex >= 0 && (schedule[0]?.pmiPayment ?? 0) > 0) {
    metrics.pmiCancelIndex = pmiCancelIndex;
  }
  return metrics;
}

export function computeAffordabilityMetrics(inputs: Partial<AffordabilityInputs>) {
  const result = calculateAffordability(inputs as AffordabilityInputs);
  return {
    maxHomePrice: Math.round(result.maxHomePrice),
    totalMonthlyCost: Math.round(result.totalMonthlyCost),
    bindingConstraint: result.bindingConstraint,
    frontEndRatio: Math.round(result.frontEndRatio * 10) / 10,
    backEndRatio: Math.round(result.backEndRatio * 10) / 10,
  };
}

export function formatUsd(amount: number): string {
  return `$${amount.toLocaleString('en-US')}`;
}

export function metricsMatch(
  actual: TabScheduleMetrics,
  expected: Partial<TabScheduleMetrics>
): boolean {
  for (const key of Object.keys(expected) as (keyof TabScheduleMetrics)[]) {
    const exp = expected[key];
    if (exp === undefined) continue;
    if (actual[key] !== exp) return false;
  }
  return true;
}
